import { useInfiniteQuery } from 'react-query';
import { useContext } from 'react';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import { AppContext } from 'views/App/context';

import type { AccreditationBody } from '../../../_types/awb';
import type { PaginatedApiCollectionResponse } from 'types/api_response';

export async function getAccreditationBodies<T extends AccreditationBody>(
  orgId: string,
  params = {},
  pageParam?: string,
  signal?: AbortSignal,
  useRUIData = false
): Promise<PaginatedApiCollectionResponse<T>> {
  // Use non-org-specific API when awbRUIData feature is enabled
  const url = useRUIData
    ? `${baseApiUrl()}/awb/accreditation-body/v1/accreditation-bodies`
    : `${baseApiUrl()}/awb/accreditation-body/v1/organizations/${orgId}/accreditation-bodies`;

  const paramString = createParamString(params);

  const fullUrl = pageParam || `${url}?${paramString}`;

  const data: PaginatedApiCollectionResponse<T> = await fetcher({
    url: fullUrl,
    method: 'get',
    options: { signal },
  });

  return data as PaginatedApiCollectionResponse<T>;
}

export default function useAccreditingBodies<T extends AccreditationBody>(
  orgId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { isFeatureEnabled } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData');

  return useInfiniteQuery<PaginatedApiCollectionResponse<T>, Error>(
    ['accrediting_bodies', orgId, params, useRUIData],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return getAccreditationBodies<T>(orgId, combinedParams, undefined, signal, useRUIData);
    },
    {
      ...options,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextToken && lastPage.content.length > 0) {
          return lastPage.nextToken;
        }
      },
    }
  );
}
